import React, { useState, createRef, useRef, useEffect } from "react"
import styled from "@emotion/styled"
import Img from "gatsby-image"
import { graphql } from "gatsby"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import SEO from "../components/seo"
import Hero from "../components/hero"

import Reveal from "../components/reveal"

const StyledH1 = styled.h1``

const MissionSection = styled.section`
  position: relative;
  z-index: 1;
  @media (min-width: 768px) {
  }
`

const Beliefs = styled.div``

const Belief = styled.div`
  position: relative;
  margin-top: 30px;
  margin-bottom: 30px;
  @media (min-width: 768px) {
    margin-top: 60px;
    margin-bottom: 60px;
  }
`

const StyledHero = styled(Hero)``

const Rounded = styled.div`
  width: 150vw;
  height: calc(175px + 5%);
  border-radius: 42% 58% 70% 30% / 45% 45% 55% 55%;
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translate(-50%, 150px);
  z-index: 1;
  background-color: #fff;
`

export default function Page({
  data, // this prop will be injected by the GraphQL query below.
}) {
  const { heroImageDesktop, heroImageMobile, moodImage } = data // data.markdownRemark holds our post data
  const sources = [
    heroImageMobile.childImageSharp.fluid,
    {
      ...heroImageDesktop.childImageSharp.fluid,
      media: `(min-width: 768px)`,
    },
  ]

  const heroRef = createRef(null)
  const sectionRef = useRef(null)
  if (typeof window !== "undefined") {
    gsap.registerPlugin(ScrollTrigger)
  }

  useEffect(() => {
    gsap.to(heroRef.current, {
      yPercent: 50,
      ease: "none",
      scrollTrigger: {
        trigger: sectionRef.current,
        start: "top top", // the default values
        // end: "bottom top",
        scrub: true,
      },
    })
    return () => {}
  }, [sectionRef, heroRef])

  return (
    <>
      <SEO
        title="About us"
        description="We are a creative agency with digital DNA. We help companies embrace change to increase sales, enhance customer experience, and grow brand awareness."
        image={heroImageDesktop.childImageSharp.fluid.src}
      />

      <section className="section-hero position-relative" ref={sectionRef}>
        {sources && (
          <StyledHero
            ref={heroRef}
            img={
              <Img
                objectFit="cover"
                objectPosition="50% 50%"
                fluid={sources}
                alt="Chunk Creative Agency - Amsterdam | London"
              />
            }
          >
            <div className="container-fluid d-flex flex-column h-100 justify-content-center black">
              <div className="row">
                <div className="col-12 text-center">
                  <StyledH1 className="display-2 font-weight-bolder mb-1 mb-md-2 mb-lg-4">
                    <span>New! Chunk custom built CMS</span>
                  </StyledH1>
                </div>
              </div>
              <div className="d-none d-lg-flex row justify-content-center">
                <div className="col-12 col-md-7 text-center">
                  <p>
                    Now you can also have your corporate website or campaign
                    landing page brought to life by Chunk’s Content Management
                    System.
                  </p>
                </div>
              </div>
            </div>
          </StyledHero>
        )}
        <Rounded></Rounded>
      </section>

      <Beliefs className="bg-white">
        <div className="container-fluid">
          <Belief className="row">
            <div className="col-12">
              <div className="row row__content">
                <div className="col-12 col-md-6 col-lg-5">
                  <Reveal>
                    <h4 className="h6 font-weight-bolder mb-2">For who?</h4>
                    <p>
                      Companies or organisations that look for a one-stop-shop
                      solution when it comes to bringing the design or campaign
                      landing page to life online. It’s quick to set-up, very
                      affordable and we deliver personal service, before, during
                      and afterwards.
                    </p>
                  </Reveal>
                </div>
                <div className="col-12 col-md-6 offset-lg-1 col-lg-5">
                  <Reveal>
                    <h4 className="h6 font-weight-bolder mt-3 mt-lg-0 mb-2">
                      Technology
                    </h4>
                    <p>
                      The CMS is based on the powerful open source system
                      Prismic. It’s very flexible, used globally by thousands of
                      websites and it’s “headless”. This means a superfast
                      website or page because it is fully generated at the
                      server side and published as a client side website or
                      page. So the user experiences a seamless experience that
                      doesn’t require any contact with the server. We create the
                      content in Next.js which is extremely flexible in its
                      possibilities. Adding or changing templates is easy. Plus
                      the CMS publishes 100% mobile ready pages, putting the
                      majority of your users first row.
                    </p>
                  </Reveal>
                </div>
              </div>
            </div>
          </Belief>
        </div>
      </Beliefs>
    </>
  )
}

export const pageQuery = graphql`
  query {
    heroImageDesktop: file(
      relativePath: { eq: "home/ChunkHeader_Desktop_AboutUs.jpg" }
    ) {
      childImageSharp {
        fluid(maxWidth: 5000) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    heroImageMobile: file(
      relativePath: { eq: "home/ChunkHeader_Mobile_AboutUs.jpg" }
    ) {
      childImageSharp {
        fluid(maxWidth: 1600) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    moodImage: file(relativePath: { eq: "mood/ChunkMood_5.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1600) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
  }
`
